import React, { useState, useCallback, createContext, type ReactNode } from 'react';
import noop from 'lodash/noop';

type FieldName = string;

type RegisterEntryPointLoadProps = {
	fieldName: FieldName;
	onEntryPointLoad: () => void;
};

type DecomposedFieldEntryPointContextType = {
	registerEntryPointLoad: ({
		fieldName,
		onEntryPointLoad,
	}: RegisterEntryPointLoadProps) => () => void;
	loadAllEntryPoints: () => void;
	getIsEntryPointsLoaded: () => boolean;
};

export const DecomposedFieldEntryPointContext = createContext<DecomposedFieldEntryPointContextType>(
	{
		registerEntryPointLoad: ({
			fieldName: _fieldName,
			onEntryPointLoad: _onEntryPointLoad,
		}: RegisterEntryPointLoadProps) => noop,
		loadAllEntryPoints: () => noop,
		getIsEntryPointsLoaded: () => false,
	},
);

type DecomposedFiledEntryPointStateType = {
	entryPointLoadMap: Map<FieldName, () => void>;
	isLoaded: boolean;
};

export const DecomposedFieldEntryPointProvider = ({ children }: { children: ReactNode }) => {
	const [decomposedFieldEntryPointState, setDecomposedFieldEntryPointState] =
		useState<DecomposedFiledEntryPointStateType>({
			entryPointLoadMap: new Map(),
			isLoaded: false,
		});

	const registerEntryPointLoad = useCallback(
		({ fieldName, onEntryPointLoad }: RegisterEntryPointLoadProps) => {
			setDecomposedFieldEntryPointState((prev) => {
				const previousEntryPointLoadMap = prev.entryPointLoadMap;
				const previousIsLoaded = prev.isLoaded;
				const newEntryPointLoadMap = new Map(previousEntryPointLoadMap);
				newEntryPointLoadMap.set(fieldName, onEntryPointLoad);

				const newState = {
					entryPointLoadMap: newEntryPointLoadMap,
					isLoaded: previousIsLoaded,
				};

				return newState;
			});

			return () => {
				setDecomposedFieldEntryPointState((prev) => {
					const previousEntryPointLoadMap = prev.entryPointLoadMap;
					const previousIsLoaded = prev.isLoaded;
					const newEntryPointLoadMap = new Map(previousEntryPointLoadMap);
					newEntryPointLoadMap.delete(fieldName);
					const newState = {
						entryPointLoadMap: newEntryPointLoadMap,
						isLoaded: previousIsLoaded,
					};

					return newState;
				});
			};
		},
		[],
	);

	const loadAllEntryPoints = useCallback(() => {
		if (decomposedFieldEntryPointState.isLoaded) {
			return;
		}

		setDecomposedFieldEntryPointState((prev) => {
			const previousEntryPointLoadMap = prev.entryPointLoadMap;

			if (previousEntryPointLoadMap.size === 0) {
				return prev;
			}

			previousEntryPointLoadMap.forEach((entryPointLoad) => entryPointLoad());
			const newState = {
				entryPointLoadMap: previousEntryPointLoadMap,
				isLoaded: true,
			};

			return newState;
		});
	}, [decomposedFieldEntryPointState.isLoaded]);

	const getIsEntryPointsLoaded = useCallback(() => {
		return decomposedFieldEntryPointState.isLoaded;
	}, [decomposedFieldEntryPointState]);

	return (
		<DecomposedFieldEntryPointContext.Provider
			value={{ registerEntryPointLoad, loadAllEntryPoints, getIsEntryPointsLoaded }}
		>
			{children}
		</DecomposedFieldEntryPointContext.Provider>
	);
};
